/**
 * v1.2 Batch 2-B：跨步骤客户数据 + prefill hook
 *
 * 销售流程 3-8 阶段的所有创建表单（合同 / 发票 / 收款 / 邮寄 / 资料 / 订单）
 * 都需要从 customers 表读：姓名 / 手机 / 微信 / 地址 / 产品类型 / 报名版型 …
 * 用户填了一次的字段不允许再填，改了 customer 主表后所有下游表单立刻同步。
 *
 * 用法：
 *   const { customer, prefill, isLoading } = useCustomerData(customerId);
 *   if (!customer) return null;
 *   <Input value={form.realName ?? prefill.realName} ... />
 *
 * React Query 共享 cache，多个组件同 customerId 仅一次请求；CustomerDetail
 * 调 qc.invalidateQueries(['customer', String(id)]) 后所有页面立刻刷新。
 */
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { $ } from './api';

export interface CustomerRecord {
  id: number;
  nickname?: string | null;
  realName?: string | null;
  phone?: string | null;
  wechatId?: string | null;
  wechatNickname?: string | null;
  region?: string | null;
  email?: string | null;
  education?: string | null;
  gender?: string | null;
  age?: number | null;
  birthday?: string | null;
  currentStage?: string | null;
  status?: string | null;
  ownerId?: number | null;
  managerId?: number | null;
  teamId?: number | null;
  demandType?: string | null;
  intent?: string | null;
  salesFlowStep?: string | null;
  depositChosen?: 'with' | 'without' | null;
  contractId?: number | null;
  totalPaid?: string | number | null;
  totalDue?: string | number | null;
  // 任意扩展字段（避免每次新增字段都改类型）
  [k: string]: any;
}

/** 各步骤创建表单可直接挑用的预填字段（蛇形 → 驼峰统一） */
export interface CustomerPrefill {
  customerId: number;
  realName: string;
  nickname: string;
  phone: string;
  wechat: string;
  email: string;
  address: string;          // 邮寄地址（region 字段；后续若细分省市，从这里扩展）
  demandType: string;        // 产品需求类型 — 教培/证书/电商等
  productType: string;       // 报名版型（demandType 的人类可读别名）
  contractId: number | null;
  totalDueCny: number;       // 已记录的应收金额（合同金额或订单金额回填）
  ownerId: number | null;
}

/** 单一客户数据 hook，跨步骤复用 + React Query 共享 cache */
export function useCustomerData(customerId?: number | string | null) {
  const idNum = customerId != null ? Number(customerId) : NaN;
  const enabled = Number.isFinite(idNum) && idNum > 0;

  const query = useQuery<CustomerRecord>({
    queryKey: ['customer', String(idNum)],
    queryFn: () => $.get(`/customers/${idNum}`),
    enabled,
    staleTime: 30_000,
  });

  const prefill = useMemo<CustomerPrefill | null>(() => {
    if (!query.data) return null;
    const c = query.data;
    const demand = c.demandType ?? '';
    return {
      customerId: Number(c.id),
      realName: (c.realName ?? c.nickname ?? '').trim(),
      nickname: (c.nickname ?? '').trim(),
      phone: (c.phone ?? '').trim(),
      wechat: (c.wechatId ?? c.wechatNickname ?? '').trim(),
      email: (c.email ?? '').trim(),
      address: (c.region ?? '').trim(),
      demandType: demand,
      productType: demandTypeLabel(demand),
      contractId: c.contractId ?? null,
      totalDueCny: Number(c.totalDue ?? 0),
      ownerId: c.ownerId ?? null,
    };
  }, [query.data]);

  return {
    customer: query.data ?? null,
    prefill,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

function demandTypeLabel(d: string): string {
  switch (d) {
    case 'work_cert':       return '职业资格证';
    case 'fulltime_shop':   return '全职电商';
    case 'sideline_shop':   return '副业电商';
    case 'brand_overseas':  return '品牌出海';
    default:                return d || '';
  }
}

/** 把 prefill 字段填进 form state 的工具（用于 mount 时 setForm） */
export function applyPrefillToForm<T extends Record<string, any>>(
  prefill: CustomerPrefill | null,
  formSetter: (next: Partial<T>) => void,
  mapping: Partial<Record<keyof CustomerPrefill, keyof T>>,
) {
  if (!prefill) return;
  const next: Partial<T> = {};
  for (const [pk, fk] of Object.entries(mapping)) {
    if (!fk) continue;
    const v = (prefill as any)[pk];
    if (v == null || v === '') continue;
    (next as any)[fk] = v;
  }
  if (Object.keys(next).length > 0) formSetter(next);
}
